import type { ErrorCode } from '@ps/web-core-enums';

import type { ErrorBody, RequestError } from '@swf/core/errors/RequestError';
import type { ModelConfig, Slicer } from '@swf/core/store';
import type { ResponseHeaders } from '@swf/core/types/Request';
import type { ErrorCollection } from '@swf/core/types/Response';

import login from './login';
import logout from './logout';
import pinReset from './pinReset';
import register from './register';
import signEula from './signEula';
import verifySession from './verifySession';

export type AuthState = ErrorCollection &
  Partial<ResponseHeaders> & {
    code?: ErrorCode;
    message?: string;
    userSite?: string;
    client?: string;
    country?: string;
  };

export interface AuthError extends ErrorBody {
  code: ErrorCode;
  selfExclusionEndTime?: string;
  longSessionCoolOff?: string;
  panicButtonExclusionEndTime?: string;
  loginCoolOffLimit?: string;
}

const reducers = {
  clear: (): AuthState => ({}),
  error: (state: AuthState, { headers, body }: RequestError<AuthError>): AuthState => ({
    ...state,
    ...headers,
    ...body
  }),
  success: (state: AuthState, payload: Partial<AuthState>): AuthState => payload
};

const effects = {
  register,
  login,
  signEula,
  verifySession,
  logout,
  pinReset
};

export const selectors = (slice: Slicer<AuthState>) => ({
  get() {
    return slice;
  },
  getStatusCode() {
    return slice((state) => state.statusCode);
  },
  getStatusSubcode() {
    return slice((state) => state.statusSubcode);
  },
  getResponse() {
    return slice((state) => ({
      fieldErrors: state.fieldErrors,
      globalErrors: state.globalErrors,
      serverMessage: state.message,
      statusCode: state.statusCode,
      statusSubcode: state.statusSubcode,
      userSite: state.userSite,
      errorCode: state.code,
      client: state.client,
      country: state.country
    }));
  }
});

interface AuthModel extends ModelConfig<AuthState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const auth: AuthModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default auth;
